import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { projectTeamRoleKeys } from "@/lib/query";

export interface ProjectTeamRole {
  id: string;
  key: string;
  label: string;
  isSystem: boolean;
  sortOrder: number;
}

export function useProjectTeamRoles() {
  return useQuery({
    queryKey: projectTeamRoleKeys.all,
    queryFn: () => apiFetch<ProjectTeamRole[]>("/project-team-roles"),
  });
}

export function useCreateProjectTeamRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => apiPost<ProjectTeamRole>("/project-team-roles", { label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamRoleKeys.all });
      toast.success("Role created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProjectTeamRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) =>
      apiPatch<ProjectTeamRole>(`/project-team-roles/${id}`, { label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamRoleKeys.all });
      toast.success("Role updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteProjectTeamRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/project-team-roles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectTeamRoleKeys.all });
      toast.success("Role deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
